<script setup>
import { ref, computed, onMounted, defineProps, watch, nextTick, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import axios from 'axios'
import RecordEditor from './RecordEditor.vue'
import TableSelector from './TableSelector.vue'

const props = defineProps({
  tableName: String,
  popupId: String,
  persistedFilters: Object
})

const emit = defineEmits(['record-selected', 'record-deselected', 'table-changed', 'diagnostics-toggle', 'filters-changed'])

const route = useRoute()

const data = ref([])
const columns = ref([])
const loading = ref(false)
const currentPage = ref(1)
const perPage = ref(25)
const totalRows = ref(0)
const searchQuery = ref('')
const sortBy = ref('')
const sortOrder = ref('asc')

// Advanced filtering
const showFilters = ref(false)
const filterConditions = ref([])
const nextFilterId = ref(1)
const showEditModal = ref(false)
const showCreateModal = ref(false)
const showRecordEditor = ref(false)
const editingRow = ref(null)
const newRow = ref({})
const isNewRecord = ref(false)
const selectedRowId = ref(null)
const customPerPage = ref(null)
const customInput = ref(null)

// Foreign key functionality - now handled by backend
const foreignKeys = ref({})

// Quick command sidebar state
const showQuickCommand = ref(false)

// Request timing
const executionTime = ref(null)
const diagnostics = ref(null)
const showDiagnostics = ref(false)

// JOIN toggle
const enableJoins = ref(false) // Default to Raw mode for better performance

// Column visibility and search functionality
const showColumnControls = ref(false)
const columnFilterQuery = ref('')
const columnSearchQuery = ref('')
const visibleColumns = ref(new Set())
const matchedColumns = ref([])
const currentMatchIndex = ref(-1)

// Row selection and export functionality
const selectedRows = ref(new Set())
const showExportDropdown = ref(false)

const totalPages = computed(() => {
  const actualPerPage = perPage.value === 'custom' ? customPerPage.value : perPage.value
  return Math.ceil(totalRows.value / parseInt(actualPerPage || 25))
})

const loadTableData = async () => {
  if (!props.tableName) return
  
  loading.value = true
  try {
    const params = {
      page: currentPage.value,
      per_page: parseInt(perPage.value === 'custom' ? customPerPage.value : perPage.value),
      search: searchQuery.value,
      sort_by: sortBy.value,
      sort_order: sortOrder.value,
      enable_joins: enableJoins.value.toString()
    }

    // Add advanced filters to params
    if (filterConditions.value.length > 0) {
      params.filters = JSON.stringify(filterConditions.value.filter(f => f.column && f.value))
    }

    console.log('Loading table data with params:', params)
    const response = await axios.get(`/api/database-tables/${props.tableName}/data`, { params })
    // console.log('API response:', response.data)
    data.value = response.data.data
    columns.value = response.data.columns
    totalRows.value = response.data.total
    foreignKeys.value = response.data.foreign_keys || {}
    executionTime.value = response.data.execution_time_ms || null
    diagnostics.value = response.data.diagnostics || null
  } catch (error) {
    console.error('Error loading table data:', error)
  } finally {
    loading.value = false
  }
}

const deleteRow = async (rowId) => {
  try {
    await axios.delete(`/api/database-tables/${props.tableName}/data/${rowId}`)
    await loadTableData()
  } catch (error) {
    console.error('Error deleting row:', error)
  }
}

const updateRow = async () => {
  try {
    const primaryKey = columns.value.find(col => col.key === 'PRI')?.name
    if (!primaryKey) {
      alert('No primary key found for this table')
      return
    }

    await axios.put(`/api/database-tables/${props.tableName}/data/${editingRow.value[primaryKey]}`, editingRow.value)
    await loadTableData()
    showEditModal.value = false
    editingRow.value = null
  } catch (error) {
    console.error('Error updating row:', error)
  }
}

const createRow = async () => {
  try {
    await axios.post(`/api/database-tables/${props.tableName}/data`, newRow.value)
    await loadTableData()
    showCreateModal.value = false
    resetNewRow()
  } catch (error) {
    console.error('Error creating row:', error)
  }
}

const editRow = (row) => {
  editingRow.value = { ...row }
  isNewRecord.value = false
  showRecordEditor.value = true
}

const openCreateModal = () => {
  resetNewRow()
  isNewRecord.value = true
  showRecordEditor.value = true
}

const openCreateModalOld = () => {
  resetNewRow()
  showCreateModal.value = true
}

const resetNewRow = () => {
  newRow.value = {}
  columns.value.forEach(col => {
    if (!col.autoIncrement) {
      newRow.value[col.name] = col.default || ''
    }
  })
}

const sortTable = (column) => {
  if (sortBy.value === column) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortBy.value = column
    sortOrder.value = 'asc'
  }
  currentPage.value = 1
  loadTableData()
}

const changePage = (page) => {
  currentPage.value = page
  loadTableData()
}

const search = () => {
  currentPage.value = 1
  loadTableData()
}

const handleRecordSaved = () => {
  showRecordEditor.value = false
  loadTableData()
}

const closeRecordEditor = () => {
  showRecordEditor.value = false
  editingRow.value = null
}

const selectRow = (row) => {
  console.log('selectRow called with:', row)
  const primaryKey = columns.value.find(col => col.key === 'PRI')?.name
  console.log('Primary key found:', primaryKey)
  if (primaryKey) {
    selectedRowId.value = row[primaryKey]
    editingRow.value = { ...row }
                console.log('Row selected:', selectedRowId.value)
      emit('record-selected', row, columns.value)
  } else {
    console.log('No primary key found, using first column')
    // Fallback to first column if no primary key
    const firstColumn = columns.value[0]?.name
    if (firstColumn) {
      selectedRowId.value = row[firstColumn]
      editingRow.value = { ...row }
                      console.log('Row selected with fallback:', selectedRowId.value)
        emit('record-selected', row, columns.value)
    }
  }
}

const closeRecordViewer = () => {
  selectedRowId.value = null
  editingRow.value = null
  emit('record-deselected')
}

const editSelectedRecord = () => {
  if (editingRow.value) {
    isNewRecord.value = false
    showRecordEditor.value = true
  }
}

const handleForeignKeyDetails = (details) => {
  console.log('Foreign key details:', details)
  // You could open a popup or show details in a modal here
  // For now, we'll just log the details
}

const isRowSelectedForViewing = (row) => {
  const primaryKey = columns.value.find(col => col.key === 'PRI')?.name
  if (primaryKey) {
    return selectedRowId.value === row[primaryKey]
  }
  // Fallback to first column
  const firstColumn = columns.value[0]?.name
  return firstColumn && selectedRowId.value === row[firstColumn]
}

const applyCustomPerPage = () => {
  if (customPerPage.value) {
    const value = parseInt(customPerPage.value)
    if (value >= 1 && value <= 20000) {
      perPage.value = value
      currentPage.value = 1 // Reset to first page
      loadTableData()
    } else {
      alert('Please enter a number between 1 and 1000')
      customPerPage.value = null
    }
  }
}

// Load persisted filter state
const loadPersistedFilters = (persistedState) => {
  if (persistedState) {
    filterConditions.value = persistedState.conditions || []
    showFilters.value = persistedState.showFilters || false
    nextFilterId.value = persistedState.nextFilterId || 1
  }
}

watch(() => props.tableName, () => {
  currentPage.value = 1
  searchQuery.value = ''
  sortBy.value = ''
  sortOrder.value = 'asc'
  loadTableData()
})

// Watch for persisted filters changes and load them
watch(() => props.persistedFilters, (newFilters) => {
  if (newFilters && props.popupId) {
    loadPersistedFilters(newFilters)
  }
}, { immediate: true })

watch(() => perPage.value, (newValue) => {
  if (newValue !== 'custom') {
    customPerPage.value = null
  } else {
    // Auto-focus the custom input when custom is selected
    nextTick(() => {
      if (customInput.value) {
        customInput.value.focus()
      }
    })
  }
})

onMounted(() => {
  loadTableData()
  window.addEventListener('keydown', handleGlobalKeydown)
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleGlobalKeydown)
  document.removeEventListener('click', handleClickOutside)
})

// Helper function to check if column has foreign key
const hasForeignKey = (columnName) => {
  return foreignKeys.value && foreignKeys.value[columnName]
}

// Get foreign key display value
const getForeignKeyDisplay = (row, columnName) => {
  const displayKey = `${columnName}_display`
  return row[displayKey] || `ID: ${row[columnName]}`
}

// Quick command functions
const toggleQuickCommand = () => {
  showQuickCommand.value = !showQuickCommand.value
}

const handleTableSelect = (tableName) => {
  showQuickCommand.value = false
  emit('table-changed', tableName)
}

const toggleDiagnostics = () => {
  showDiagnostics.value = !showDiagnostics.value
  
  // Emit event for popup windows to handle
  emit('diagnostics-toggle', {
    show: showDiagnostics.value,
    diagnostics: diagnostics.value,
    tableName: props.tableName,
    executionTime: executionTime.value
  })
}

const toggleJoins = () => {
  enableJoins.value = !enableJoins.value
  loadTableData() // Reload data with new JOIN setting
}

// Advanced Filter management
const toggleFilters = () => {
  showFilters.value = !showFilters.value
  if (showFilters.value && filterConditions.value.length === 0) {
    addFilterCondition()
  }
  
  // Emit filter changes for persistence
  if (props.popupId) {
    const filterState = {
      conditions: filterConditions.value,
      showFilters: showFilters.value,
      nextFilterId: nextFilterId.value
    }
    emit('filters-changed', filterState)
  }
}

const addFilterCondition = () => {
  filterConditions.value.push({
    id: nextFilterId.value++,
    enabled: true,
    column: '',
    operator: 'equals',
    value: '',
    value2: '', // For BETWEEN operator
    logic: 'AND' // AND/OR
  })
  
  // Emit filter changes for persistence
  if (props.popupId) {
    const filterState = {
      conditions: filterConditions.value,
      showFilters: showFilters.value,
      nextFilterId: nextFilterId.value
    }
    emit('filters-changed', filterState)
  }
}

const removeFilterCondition = (id) => {
  const index = filterConditions.value.findIndex(f => f.id === id)
  if (index > -1) {
    filterConditions.value.splice(index, 1)
    applyFilters()
  }
}

const clearAllFilters = () => {
  filterConditions.value = []
  applyFilters()
}

const applyFilters = () => {
  currentPage.value = 1
  loadTableData()
  
  // Emit filter changes for persistence
  if (props.popupId) {
    const filterState = {
      conditions: filterConditions.value,
      showFilters: showFilters.value,
      nextFilterId: nextFilterId.value
    }
    emit('filters-changed', filterState)
  }
}

const getFilterOperatorOptions = (column) => {
  const baseOptions = [
    { value: 'equals', label: '=' },
    { value: 'not_equals', label: '!=' },
    { value: 'contains', label: 'CONTAINS' },
    { value: 'not_contains', label: 'NOT CONTAINS' },
    { value: 'starts_with', label: 'STARTS WITH' },
    { value: 'ends_with', label: 'ENDS WITH' },
    { value: 'is_null', label: 'IS NULL' },
    { value: 'is_not_null', label: 'IS NOT NULL' },
    { value: 'in', label: 'IN' },
    { value: 'not_in', label: 'NOT IN' },
    { value: 'between', label: 'BETWEEN' }
  ]

  // Add numeric/date operators
  if (column && column.type && (
    column.type.includes('int') || 
    column.type.includes('decimal') || 
    column.type.includes('float') ||
    column.type.includes('date') ||
    column.type.includes('time')
  )) {
    baseOptions.push(
      { value: 'greater_than', label: '>' },
      { value: 'less_than', label: '<' },
      { value: 'greater_equal', label: '>=' },
      { value: 'less_equal', label: '<=' }
    )
  }

  return baseOptions
}

const getActiveFiltersCount = computed(() => {
  return filterConditions.value.filter(f => f.enabled && f.column && f.value).length
})

// Column management computed properties
const filteredColumns = computed(() => {
  if (!columnFilterQuery.value.trim()) {
    return columns.value
  }
  const query = columnFilterQuery.value.toLowerCase()
  return columns.value.filter(col => col.name.toLowerCase().includes(query))
})

const displayedColumns = computed(() => {
  if (visibleColumns.value.size === 0) {
    return columns.value // Show all columns if none specifically selected
  }
  return columns.value.filter(col => visibleColumns.value.has(col.name))
})

const recordsCount = computed(() => {
  return {
    total: totalRows.value,
    showing: data.value.length,
    page: currentPage.value,
    totalPages: totalPages.value,
    perPage: perPage.value === 'custom' ? customPerPage.value : perPage.value
  }
})

// Row selection computed properties
const isAllRowsSelected = computed(() => {
  return data.value.length > 0 && selectedRows.value.size === data.value.length
})

const isSomeRowsSelected = computed(() => {
  return selectedRows.value.size > 0 && selectedRows.value.size < data.value.length
})

const selectedRowsData = computed(() => {
  const primaryKey = columns.value.find(col => col.key === 'PRI')?.name || columns.value[0]?.name
  if (!primaryKey) return []
  
  return data.value.filter(row => selectedRows.value.has(row[primaryKey]))
})

const needsSecondValue = (operator) => {
  return operator === 'between'
}

const needsNoValue = (operator) => {
  return ['is_null', 'is_not_null'].includes(operator)
}

const getColumnByName = (columnName) => {
  return columns.value.find(c => c.name === columnName)
}

// Quick filter functionality
const hasFixtureIdColumn = computed(() => {
  return columns.value.some(col => col.name === 'fixture_id')
})

const currentFixtureId = computed(() => {
  return route.params.id || 'N/A'
})

const addFixtureIdFilter = () => {
  // Check if fixture_id filter already exists
  const existingFilter = filterConditions.value.find(f => f.column === 'fixture_id')
  if (existingFilter) {
    // Update existing filter with current fixture ID
    existingFilter.value = currentFixtureId.value
    existingFilter.enabled = true
  } else {
    // Add new fixture_id filter
    filterConditions.value.push({
      id: nextFilterId.value++,
      enabled: true,
      column: 'fixture_id',
      operator: 'equals',
      value: currentFixtureId.value,
      value2: '',
      logic: 'AND'
    })
  }
  
  // Apply the filters immediately
  applyFilters()
}

const handleQuickCommandKeydown = (event) => {
  // Close sidebar on Escape
  if (event.key === 'Escape') {
    showQuickCommand.value = false
  }
}

// Column management functions
const toggleColumnControls = () => {
  showColumnControls.value = !showColumnControls.value
  if (showColumnControls.value) {
    // Initialize visible columns with all columns if empty
    if (visibleColumns.value.size === 0) {
      columns.value.forEach(col => visibleColumns.value.add(col.name))
    }
  }
}

const toggleColumnVisibility = (columnName) => {
  if (visibleColumns.value.has(columnName)) {
    visibleColumns.value.delete(columnName)
  } else {
    visibleColumns.value.add(columnName)
  }
}

const showAllColumns = () => {
  visibleColumns.value.clear()
  columns.value.forEach(col => visibleColumns.value.add(col.name))
}

const hideAllColumns = () => {
  visibleColumns.value.clear()
}

const searchColumns = () => {
  if (!columnSearchQuery.value.trim()) {
    matchedColumns.value = []
    currentMatchIndex.value = -1
    return
  }

  const query = columnSearchQuery.value.toLowerCase()
  matchedColumns.value = displayedColumns.value
    .map((col, index) => ({ column: col, index }))
    .filter(item => item.column.name.toLowerCase().includes(query))
  
  currentMatchIndex.value = matchedColumns.value.length > 0 ? 0 : -1
  
  if (matchedColumns.value.length > 0) {
    scrollToColumn(matchedColumns.value[0].column.name)
  }
}

const nextMatch = () => {
  if (matchedColumns.value.length === 0) return
  
  currentMatchIndex.value = (currentMatchIndex.value + 1) % matchedColumns.value.length
  scrollToColumn(matchedColumns.value[currentMatchIndex.value].column.name)
}

const previousMatch = () => {
  if (matchedColumns.value.length === 0) return
  
  currentMatchIndex.value = currentMatchIndex.value === 0 
    ? matchedColumns.value.length - 1 
    : currentMatchIndex.value - 1
  scrollToColumn(matchedColumns.value[currentMatchIndex.value].column.name)
}

const scrollToColumn = (columnName) => {
  // Find the column header element and scroll to it
  const columnHeader = document.querySelector(`[data-column-name="${columnName}"]`)
  if (columnHeader) {
    columnHeader.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'nearest', 
      inline: 'center' 
    })
    
    // Highlight the column temporarily
    columnHeader.classList.add('column-highlight')
    setTimeout(() => {
      columnHeader.classList.remove('column-highlight')
    }, 2000)
  }
}

const clearColumnSearch = () => {
  columnSearchQuery.value = ''
  matchedColumns.value = []
  currentMatchIndex.value = -1
}

// Row selection functions
const getRowId = (row) => {
  const primaryKey = columns.value.find(col => col.key === 'PRI')?.name || columns.value[0]?.name
  return primaryKey ? row[primaryKey] : null
}

const toggleRowSelection = (row) => {
  const rowId = getRowId(row)
  if (!rowId) return
  
  if (selectedRows.value.has(rowId)) {
    selectedRows.value.delete(rowId)
  } else {
    selectedRows.value.add(rowId)
  }
}

const toggleAllRows = () => {
  if (isAllRowsSelected.value) {
    selectedRows.value.clear()
  } else {
    data.value.forEach(row => {
      const rowId = getRowId(row)
      if (rowId) selectedRows.value.add(rowId)
    })
  }
}

const isRowSelected = (row) => {
  const rowId = getRowId(row)
  return rowId ? selectedRows.value.has(rowId) : false
}

// Export functions
const exportAsSQL = (type) => {
  if (selectedRowsData.value.length === 0) {
    alert('Please select rows to export')
    return
  }

  let sql = ''
  const tableName = props.tableName
  const rows = selectedRowsData.value
  const cols = displayedColumns.value

  switch (type) {
    case 'insert':
      sql = generateInsertSQL(tableName, rows, cols)
      break
    case 'update':
      sql = generateUpdateSQL(tableName, rows, cols)
      break
    case 'select':
      sql = generateSelectSQL(tableName, rows, cols)
      break
    case 'delete':
      sql = generateDeleteSQL(tableName, rows)
      break
  }

  copyToClipboard(sql)
  showExportDropdown.value = false
  alert(`${type.toUpperCase()} SQL copied to clipboard!`)
}

const generateInsertSQL = (tableName, rows, cols) => {
  const columnNames = cols.map(col => col.name)
  const values = rows.map(row => {
    const rowValues = columnNames.map(col => {
      const value = row[col]
      if (value === null || value === undefined) return 'NULL'
      if (typeof value === 'string') return `'${value.replace(/'/g, "''")}'`
      return value
    })
    return `(${rowValues.join(', ')})`
  })

  return `-- INSERT statements for ${rows.length} selected rows from ${tableName}
-- Generated on ${new Date().toISOString()}

INSERT INTO \`${tableName}\` (${columnNames.map(col => `\`${col}\``).join(', ')})
VALUES
${values.join(',\n')};`
}

const generateUpdateSQL = (tableName, rows, cols) => {
  const primaryKey = columns.value.find(col => col.key === 'PRI')?.name
  if (!primaryKey) {
    return '-- Error: No primary key found for UPDATE statements'
  }

  const updateStatements = rows.map(row => {
    const setClauses = cols
      .filter(col => col.name !== primaryKey)
      .map(col => {
        const value = row[col.name]
        if (value === null || value === undefined) return `\`${col.name}\` = NULL`
        if (typeof value === 'string') return `\`${col.name}\` = '${value.replace(/'/g, "''")}'`
        return `\`${col.name}\` = ${value}`
      })
    
    return `UPDATE \`${tableName}\` SET ${setClauses.join(', ')} WHERE \`${primaryKey}\` = ${row[primaryKey]};`
  })

  return `-- UPDATE statements for ${rows.length} selected rows from ${tableName}
-- Generated on ${new Date().toISOString()}

${updateStatements.join('\n')}`
}

const generateSelectSQL = (tableName, rows, cols) => {
  const primaryKey = columns.value.find(col => col.key === 'PRI')?.name || columns.value[0]?.name
  const columnNames = cols.map(col => col.name)
  const whereValues = rows.map(row => row[primaryKey])

  return `-- SELECT statement for ${rows.length} selected rows from ${tableName}
-- Generated on ${new Date().toISOString()}

SELECT ${columnNames.map(col => `\`${col}\``).join(', ')}
FROM \`${tableName}\`
WHERE \`${primaryKey}\` IN (${whereValues.join(', ')});`
}

const generateDeleteSQL = (tableName, rows) => {
  const primaryKey = columns.value.find(col => col.key === 'PRI')?.name
  if (!primaryKey) {
    return '-- Error: No primary key found for DELETE statements'
  }

  const whereValues = rows.map(row => row[primaryKey])

  return `-- DELETE statement for ${rows.length} selected rows from ${tableName}
-- Generated on ${new Date().toISOString()}
-- WARNING: This will permanently delete the selected records!

DELETE FROM \`${tableName}\`
WHERE \`${primaryKey}\` IN (${whereValues.join(', ')});`
}

const exportAsCSV = () => {
  if (selectedRowsData.value.length === 0) {
    alert('Please select rows to export')
    return
  }

  const cols = displayedColumns.value
  const rows = selectedRowsData.value

  // CSV Header
  const headers = cols.map(col => col.name)
  let csvContent = headers.join(',') + '\n'

  // CSV Rows
  rows.forEach(row => {
    const values = cols.map(col => {
      let value = row[col.name]
      if (value === null || value === undefined) value = ''
      // Escape quotes and wrap in quotes if contains comma, quote, or newline
      if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
        value = '"' + value.replace(/"/g, '""') + '"'
      }
      return value
    })
    csvContent += values.join(',') + '\n'
  })

  // Download CSV
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', `${props.tableName}_selected_rows_${new Date().toISOString().split('T')[0]}.csv`)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  showExportDropdown.value = false
  alert('CSV file downloaded!')
}

const exportAsJSON = () => {
  if (selectedRowsData.value.length === 0) {
    alert('Please select rows to export')
    return
  }

  const exportData = {
    metadata: {
      table: props.tableName,
      exported_at: new Date().toISOString(),
      total_rows: selectedRowsData.value.length,
      columns: displayedColumns.value.map(col => ({
        name: col.name,
        type: col.type,
        nullable: col.nullable,
        key: col.key || null
      }))
    },
    data: selectedRowsData.value
  }

  const jsonContent = JSON.stringify(exportData, null, 2)
  const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', `${props.tableName}_selected_rows_${new Date().toISOString().split('T')[0]}.json`)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  showExportDropdown.value = false
  alert('JSON file downloaded!')
}

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text)
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed'
    textArea.style.left = '-999999px'
    textArea.style.top = '-999999px'
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()
    document.execCommand('copy')
    textArea.remove()
  }
}

// Close export dropdown when clicking outside
const handleClickOutside = (event) => {
  if (showExportDropdown.value && !event.target.closest('.export-dropdown-container')) {
    showExportDropdown.value = false
  }
}

// Clear selections when table changes
watch(() => props.tableName, () => {
  selectedRows.value.clear()
})

// Clear selections when data changes (page changes, filters applied)
watch(data, () => {
  // Keep only selections that still exist in current data
  const currentRowIds = new Set(data.value.map(row => getRowId(row)).filter(Boolean))
  const newSelection = new Set()
  selectedRows.value.forEach(rowId => {
    if (currentRowIds.has(rowId)) {
      newSelection.add(rowId)
    }
  })
  selectedRows.value = newSelection
})

// Global keyboard shortcuts
const handleGlobalKeydown = (event) => {
  // Cmd+Shift+K to toggle quick command
  if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'K') {
    event.preventDefault()
    toggleQuickCommand()
  }
  // Escape to close quick command
  if (event.key === 'Escape' && showQuickCommand.value) {
    showQuickCommand.value = false
  }
  // Escape to close column controls
  if (event.key === 'Escape' && showColumnControls.value) {
    showColumnControls.value = false
  }
}

// Watch for column search changes
watch(columnSearchQuery, () => {
  searchColumns()
})

// Initialize visible columns when columns change
watch(columns, (newColumns) => {
  if (newColumns.length > 0 && visibleColumns.value.size === 0) {
    newColumns.forEach(col => visibleColumns.value.add(col.name))
  }
}, { immediate: true })
</script>

<template>
  <div class="h-full flex min-h-0">
    <!-- Quick Command Sidebar -->
    <div 
      v-if="showQuickCommand"
      class="w-80 bg-white border-r border-gray-200 flex-shrink-0 flex flex-col"
      @keydown="handleQuickCommandKeydown"
    >
      <!-- Sidebar Header -->
      <div class="p-3 border-b border-gray-200 flex items-center justify-between">
        <h3 class="text-sm font-semibold text-gray-900">Quick Table Switch</h3>
        <button
          @click="toggleQuickCommand"
          class="text-gray-500 hover:text-gray-700 p-1 hover:bg-gray-100 rounded"
          title="Close"
        >
          <i class="fas fa-times text-xs"></i>
        </button>
      </div>
      
      <!-- Table Selector -->
      <div class="flex-1 overflow-hidden">
        <TableSelector 
          :selected-table="tableName"
          :grid-mode="false"
          :grid-tabs="[]"
          :grid-layout="'2x2'"
          :auto-focus="true"
          @table-select="handleTableSelect"
          @table-deleted="() => {}"
          @open-structure="(tableName) => handleTableSelect(tableName)"
          @open-data="(tableName) => handleTableSelect(tableName)"
          @open-popup="() => {}"
        />
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 flex flex-col min-h-0 min-w-0 relative">
      <!-- Header Controls -->
      <div class="p-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div class="flex items-center justify-between mb-2">
          <div class="flex items-center">
            <!-- Quick Command Toggle Button -->
            <button
              @click="toggleQuickCommand"
              class="mr-3 text-gray-500 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
              :class="{ 'text-blue-600 bg-blue-50': showQuickCommand }"
              title="Quick Table Switch (Cmd+Shift+K)"
            >
              <i class="fas fa-search text-sm"></i>
            </button>
            <h3 class="text-sm font-semibold text-gray-900">Table Data: {{ tableName }}</h3>
          </div>
          <div class="flex items-center space-x-2">
            <!-- Export Dropdown -->
            <div class="relative export-dropdown-container">
              <button
                @click.stop="showExportDropdown = !showExportDropdown"
                class="inline-flex items-center px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded transition-all duration-200 shadow-sm hover:shadow-md"
                :disabled="selectedRows.size === 0"
                :class="{ 'opacity-50 cursor-not-allowed': selectedRows.size === 0 }"
              >
                <i class="fas fa-download mr-1 text-xs"></i>
                Export ({{ selectedRows.size }})
                <i class="fas fa-chevron-down ml-1 text-xs"></i>
              </button>

              <!-- Export Options Dropdown -->
              <div 
                v-if="showExportDropdown" 
                class="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 export-dropdown"
                @click.stop
              >
                <!-- SQL Queries Section -->
                <div class="p-2 border-b border-gray-100">
                  <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">SQL Queries</div>
                  
                  <button
                    @click="exportAsSQL('select')"
                    class="w-full flex items-center px-3 py-2 text-xs text-gray-700 hover:bg-blue-50 rounded transition-colors"
                  >
                    <i class="fas fa-search-plus text-blue-600 mr-2 w-4"></i>
                    <span class="flex-1 text-left">SQL SELECT</span>
                  </button>
                  
                  <button
                    @click="exportAsSQL('insert')"
                    class="w-full flex items-center px-3 py-2 text-xs text-gray-700 hover:bg-green-50 rounded transition-colors"
                  >
                    <i class="fas fa-plus-circle text-green-600 mr-2 w-4"></i>
                    <span class="flex-1 text-left">SQL INSERT</span>
                  </button>
                  
                  <button
                    @click="exportAsSQL('update')"
                    class="w-full flex items-center px-3 py-2 text-xs text-gray-700 hover:bg-orange-50 rounded transition-colors"
                  >
                    <i class="fas fa-edit text-orange-600 mr-2 w-4"></i>
                    <span class="flex-1 text-left">SQL UPDATE</span>
                  </button>
                  
                  <button
                    @click="exportAsSQL('delete')"
                    class="w-full flex items-center px-3 py-2 text-xs text-gray-700 hover:bg-red-50 rounded transition-colors"
                  >
                    <i class="fas fa-trash text-red-600 mr-2 w-4"></i>
                    <span class="flex-1 text-left">SQL DELETE</span>
                  </button>
                </div>

                <!-- File Export Section -->
                <div class="p-2">
                  <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">File Export</div>
                  
                  <button
                    @click="exportAsCSV"
                    class="w-full flex items-center px-3 py-2 text-xs text-gray-700 hover:bg-purple-50 rounded transition-colors"
                  >
                    <i class="fas fa-file-csv text-purple-600 mr-2 w-4"></i>
                    <span class="flex-1 text-left">Export as CSV</span>
                  </button>
                  
                  <button
                    @click="exportAsJSON"
                    class="w-full flex items-center px-3 py-2 text-xs text-gray-700 hover:bg-indigo-50 rounded transition-colors"
                  >
                    <i class="fas fa-file-code text-indigo-600 mr-2 w-4"></i>
                    <span class="flex-1 text-left">Export as JSON</span>
                  </button>
                </div>
              </div>
            </div>

            <button
              @click="openCreateModal"
              class="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs flex items-center"
            >
              <i class="fas fa-plus mr-1"></i>
              Add Row
            </button>
          </div>
        </div>

        <div class="flex items-center space-x-2">
          <div class="flex-1">
            <div class="relative">
              <input
                v-model="searchQuery"
                @keyup.enter="search"
                type="text"
                placeholder="Search records..."
                class="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
              <i class="fas fa-search absolute left-2.5 top-2 text-gray-400 text-xs"></i>
            </div>
          </div>
          
          <select v-model="perPage" @change="loadTableData" class="border border-gray-300 rounded px-2 py-1.5 text-xs">
            <option value="10">10 per page</option>
            <option value="25">25 per page</option>
            <option value="50">50 per page</option>
            <option value="100">100 per page</option>
            <option value="custom">{{ customPerPage ? `${customPerPage} per page (custom)` : 'Custom...' }}</option>
          </select>

          <!-- Custom per page input -->
          <div v-if="perPage === 'custom'" class="flex items-center space-x-1">
            <input
              ref="customInput"
              v-model="customPerPage"
              @keyup.enter="applyCustomPerPage"
              @blur="applyCustomPerPage"
              type="number"
              min="1"
              max="1000"
              placeholder="Enter rows"
              class="w-20 border border-gray-300 rounded px-2 py-1.5 text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
            <button
              @click="applyCustomPerPage"
              class="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1.5 rounded text-xs"
            >
              Apply
            </button>
          </div>

          <button
            @click="search"
            class="bg-gray-600 hover:bg-gray-700 text-white px-2 py-1.5 rounded text-xs"
          >
            Search
          </button>

          <!-- JOIN Toggle -->
          <button
            @click="toggleJoins"
            class="px-2 py-1.5 rounded text-xs border transition-colors"
            :class="enableJoins 
              ? 'bg-green-600 hover:bg-green-700 text-white border-green-600' 
              : 'bg-gray-200 hover:bg-gray-300 text-gray-700 border-gray-300'"
            :title="enableJoins ? 'JOINs Enabled - Click to disable for faster queries' : 'JOINs Disabled - Click to enable foreign key resolution'"
          >
            <i :class="enableJoins ? 'fas fa-link' : 'fas fa-unlink'" class="mr-1"></i>
            {{ enableJoins ? 'JOINs' : 'Raw' }}
          </button>

          <!-- Column Controls Toggle -->
          <button
            @click="toggleColumnControls"
            class="px-2 py-1.5 rounded text-xs border transition-colors relative"
            :class="showColumnControls 
              ? 'bg-indigo-600 hover:bg-indigo-700 text-white border-indigo-600' 
              : 'bg-gray-200 hover:bg-gray-300 text-gray-700 border-gray-300'"
            title="Column Visibility & Search"
          >
            <i class="fas fa-columns mr-1"></i>
            Columns
            <span 
              v-if="visibleColumns.size > 0 && visibleColumns.size < columns.length" 
              class="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center"
            >
              {{ visibleColumns.size }}
            </span>
          </button>

          <!-- Filters Toggle -->
          <button
            @click="toggleFilters"
            class="px-2 py-1.5 rounded text-xs border transition-colors relative"
            :class="showFilters 
              ? 'bg-purple-600 hover:bg-purple-700 text-white border-purple-600' 
              : 'bg-gray-200 hover:bg-gray-300 text-gray-700 border-gray-300'"
            title="Toggle Column Filters"
          >
            <i class="fas fa-filter mr-1"></i>
            Filters
            <span 
              v-if="getActiveFiltersCount > 0" 
              class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center"
            >
              {{ getActiveFiltersCount }}
            </span>
          </button>
        </div>

        <!-- Advanced Filters Panel -->
        <div v-if="showFilters" class="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-lg shadow-sm">
          <div class="flex items-center justify-between mb-4">
            <h4 class="text-sm font-semibold text-gray-900 flex items-center">
              <i class="fas fa-filter mr-2 text-purple-600"></i>
              Advanced Filters
              <span v-if="getActiveFiltersCount > 0" class="ml-2 bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
                {{ getActiveFiltersCount }} active
              </span>
            </h4>
            <div class="flex items-center space-x-2">
              <button
                @click="clearAllFilters"
                v-if="getActiveFiltersCount > 0"
                class="text-xs text-red-600 hover:text-red-800 px-3 py-1.5 border border-red-300 rounded-md hover:bg-red-50 transition-colors"
              >
                <i class="fas fa-trash mr-1"></i>
                Clear All
              </button>
              <button
                @click="addFilterCondition"
                class="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md transition-colors flex items-center"
              >
                <i class="fas fa-plus mr-1"></i>
                Add Filter
              </button>
              <button
                @click="toggleFilters"
                class="text-gray-500 hover:text-gray-700 p-1.5 hover:bg-gray-200 rounded-md transition-colors"
              >
                <i class="fas fa-times text-xs"></i>
              </button>
            </div>
          </div>

          <!-- Quick Filter Buttons -->
          <div v-if="hasFixtureIdColumn" class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <i class="fas fa-bolt text-blue-600 mr-2"></i>
                <span class="text-sm font-medium text-blue-900">Quick Filters</span>
              </div>
              <button
                @click="addFixtureIdFilter"
                class="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md transition-colors flex items-center"
              >
                <i class="fas fa-filter mr-1"></i>
                Add Fixture ID Filter ({{ currentFixtureId }})
              </button>
            </div>
          </div>

          <!-- Filter Conditions -->
          <div v-if="filterConditions.length > 0" class="space-y-3">
            <div
              v-for="(filter, index) in filterConditions"
              :key="filter.id"
              class="flex items-center space-x-3 p-3 bg-white rounded border border-gray-200 shadow-sm"
            >
              <!-- Enable/Disable Checkbox -->
              <input
                v-model="filter.enabled"
                @change="applyFilters"
                type="checkbox"
                class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
              >

              <!-- Column Selection -->
              <div class="flex-shrink-0">
                <select
                  v-model="filter.column"
                  @change="filter.value = ''; filter.value2 = ''; applyFilters()"
                  class="text-xs border border-gray-300 rounded px-3 py-2 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  style="width: 120px;"
                >
                  <option value="">Select column...</option>
                  <option
                    v-for="column in columns"
                    :key="column.name"
                    :value="column.name"
                  >
                    {{ column.name }}
                  </option>
                </select>
              </div>

              <!-- Operator Selection -->
              <div class="flex-shrink-0">
                <select
                  v-model="filter.operator"
                  @change="filter.value = ''; filter.value2 = ''; applyFilters()"
                  class="text-xs border border-gray-300 rounded px-3 py-2 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  style="width: 100px;"
                >
                  <option
                    v-for="option in getFilterOperatorOptions(getColumnByName(filter.column))"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </div>

              <!-- Value Input(s) -->
              <div class="flex items-center space-x-2 flex-1 min-w-0">
                <!-- First Value -->
                <input
                  v-if="!needsNoValue(filter.operator)"
                  v-model="filter.value"
                  @keyup.enter="applyFilters"
                  @blur="applyFilters"
                  type="text"
                  class="text-xs border border-gray-300 rounded px-3 py-2 flex-1 min-w-0"
                  :placeholder="filter.operator === 'in' || filter.operator === 'not_in' ? 'value1,value2,value3' : 'Value...'"
                >

                <!-- Second Value (for BETWEEN) -->
                <template v-if="needsSecondValue(filter.operator)">
                  <span class="text-xs text-gray-500 font-medium flex-shrink-0">AND</span>
                  <input
                    v-model="filter.value2"
                    @keyup.enter="applyFilters"
                    @blur="applyFilters"
                    type="text"
                    class="text-xs border border-gray-300 rounded px-3 py-2 flex-1 min-w-0"
                    placeholder="End value..."
                  >
                </template>
              </div>

              <!-- Logic Operator (AND/OR) - Only show if not the last filter -->
              <div v-if="index < filterConditions.length - 1" class="flex-shrink-0">
                <select
                  v-model="filter.logic"
                  @change="applyFilters"
                  class="text-xs border border-gray-300 rounded px-3 py-2 bg-white hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  style="width: 60px;"
                >
                  <option value="AND">AND</option>
                  <option value="OR">OR</option>
                </select>
              </div>
              <div v-else class="w-16 flex-shrink-0"></div>

              <!-- Remove Button -->
              <button
                @click="removeFilterCondition(filter.id)"
                class="text-red-500 hover:text-red-700 p-2 hover:bg-red-100 rounded-full transition-colors flex-shrink-0"
                title="Remove filter"
              >
                <i class="fas fa-minus text-xs"></i>
              </button>
            </div>
          </div>

          <!-- Apply Button -->
          <div v-if="filterConditions.length > 0" class="mt-4 flex justify-between items-center">
            <div class="text-xs text-gray-600">
              <i class="fas fa-info-circle mr-1"></i>
              {{ filterConditions.length }} filter{{ filterConditions.length === 1 ? '' : 's' }} configured
            </div>
            <button
              @click="applyFilters"
              class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-xs font-medium transition-colors flex items-center"
            >
              <i class="fas fa-play mr-1"></i>
              Apply Filters
            </button>
          </div>

          <!-- Empty State -->
          <div v-else class="text-center py-8 text-gray-500">
            <i class="fas fa-filter text-2xl text-gray-300 mb-2"></i>
            <p class="text-sm">No filters configured</p>
            <p class="text-xs text-gray-400 mt-1">Click "Add Filter" to create your first filter condition</p>
          </div>
        </div>

        <!-- Column Controls Panel -->
        <div v-if="showColumnControls" class="mt-3 p-4 bg-indigo-50 border border-indigo-200 rounded-lg shadow-sm">
          <div class="flex items-center justify-between mb-4">
            <h4 class="text-sm font-semibold text-gray-900 flex items-center">
              <i class="fas fa-columns mr-2 text-indigo-600"></i>
              Column Management
              <span class="ml-2 bg-indigo-100 text-indigo-800 text-xs px-2 py-1 rounded-full">
                {{ visibleColumns.size }} / {{ columns.length }} visible
              </span>
            </h4>
            <div class="flex items-center space-x-2">
              <button
                @click="showAllColumns"
                class="text-xs text-indigo-600 hover:text-indigo-800 px-3 py-1.5 border border-indigo-300 rounded-md hover:bg-indigo-50 transition-colors"
              >
                <i class="fas fa-eye mr-1"></i>
                Show All
              </button>
              <button
                @click="hideAllColumns"
                class="text-xs text-gray-600 hover:text-gray-800 px-3 py-1.5 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                <i class="fas fa-eye-slash mr-1"></i>
                Hide All
              </button>
              <button
                @click="toggleColumnControls"
                class="text-gray-500 hover:text-gray-700 p-1.5 hover:bg-gray-200 rounded-md transition-colors"
              >
                <i class="fas fa-times text-xs"></i>
              </button>
            </div>
          </div>

          <!-- Two Input Controls Row -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <!-- Column Filter Input -->
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Filter Columns</label>
              <div class="relative">
                <input
                  v-model="columnFilterQuery"
                  type="text"
                  placeholder="Type to filter column list..."
                  class="w-full pl-8 pr-3 py-2 text-xs border border-indigo-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                />
                <i class="fas fa-filter absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-400 text-xs"></i>
              </div>
              <p class="text-xs text-gray-500 mt-1">Filter the column list below</p>
            </div>

            <!-- Column Search Input -->
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Find & Scroll to Column</label>
              <div class="flex space-x-2">
                <div class="relative flex-1">
                  <input
                    v-model="columnSearchQuery"
                    type="text"
                    placeholder="Search to scroll to column..."
                    class="w-full pl-8 pr-3 py-2 text-xs border border-indigo-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                  />
                  <i class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-400 text-xs"></i>
                </div>
                
                <!-- Search Navigation -->
                <div v-if="matchedColumns.length > 0" class="flex items-center space-x-1">
                  <span class="text-xs text-indigo-700 font-medium whitespace-nowrap">
                    {{ currentMatchIndex + 1 }}/{{ matchedColumns.length }}
                  </span>
                  <button
                    @click="previousMatch"
                    class="p-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-xs"
                    title="Previous match"
                  >
                    <i class="fas fa-chevron-left"></i>
                  </button>
                  <button
                    @click="nextMatch"
                    class="p-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-xs"
                    title="Next match"
                  >
                    <i class="fas fa-chevron-right"></i>
                  </button>
                </div>
                
                <!-- Clear Search -->
                <button
                  v-if="columnSearchQuery"
                  @click="clearColumnSearch"
                  class="p-1.5 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100 rounded"
                  title="Clear search"
                >
                  <i class="fas fa-times text-xs"></i>
                </button>
              </div>
              <p class="text-xs text-gray-500 mt-1">
                <span v-if="matchedColumns.length === 0 && columnSearchQuery">No matches found</span>
                <span v-else-if="matchedColumns.length > 0">{{ matchedColumns.length }} match{{ matchedColumns.length === 1 ? '' : 'es' }} found</span>
                <span v-else>Search and auto-scroll to columns</span>
              </p>
            </div>
          </div>

          <!-- Column Visibility Checkboxes -->
          <div class="max-h-48 overflow-y-auto border border-indigo-200 rounded-lg bg-white p-3">
            <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              <label
                v-for="column in filteredColumns"
                :key="column.name"
                class="flex items-center space-x-2 p-2 rounded hover:bg-indigo-50 cursor-pointer transition-colors"
                :class="{ 'bg-indigo-100': visibleColumns.has(column.name) }"
              >
                <input
                  type="checkbox"
                  :checked="visibleColumns.has(column.name)"
                  @change="toggleColumnVisibility(column.name)"
                  class="w-3 h-3 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <span class="text-xs font-medium text-gray-700 truncate" :title="column.name">
                  {{ column.name }}
                </span>
                <span class="text-xs text-gray-400">({{ column.type }})</span>
              </label>
            </div>
            
            <!-- Empty State -->
            <div v-if="filteredColumns.length === 0" class="text-center py-4 text-gray-500">
              <i class="fas fa-search text-lg text-gray-300 mb-2"></i>
              <p class="text-xs">No columns match your filter</p>
            </div>
          </div>
        </div>

        <div class="mt-1.5 text-xs text-gray-600 flex items-center justify-between">
          <span>Showing {{ recordsCount.showing }} of {{ recordsCount.total }} records</span>
          <button 
            v-if="executionTime !== null" 
            @click="toggleDiagnostics"
            class="text-gray-500 hover:text-blue-600 cursor-pointer transition-colors"
            title="Click to view detailed diagnostics"
          >
            <i class="fas fa-clock mr-1"></i>{{ executionTime }}ms
          </button>
        </div>
      </div>

      <!-- Data Table -->
      <div class="flex-1 overflow-hidden min-h-0">
        <div v-if="loading" class="flex items-center justify-center h-full">
          <i class="fas fa-spinner fa-spin text-2xl text-gray-400"></i>
        </div>

        <div v-else class="h-full overflow-auto bg-white">
          <table class="w-full divide-y divide-gray-200 text-xs" style="min-width: max-content;">
            <thead class="bg-gray-50 sticky top-0 z-10">
              <tr>
                <!-- Selection Checkbox Column -->
                <th class="px-2 py-1 w-8">
                  <input
                    type="checkbox"
                    :checked="isAllRowsSelected"
                    :indeterminate="isSomeRowsSelected"
                    @change="toggleAllRows"
                    class="w-3 h-3 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    title="Select all rows"
                  />
                </th>
                <th
                  v-for="column in displayedColumns"
                  :key="column.name"
                  @click="sortTable(column.name)"
                  :data-column-name="column.name"
                  class="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 border-r border-gray-200 min-w-0"
                  style="width: auto; max-width: 200px;"
                >
                  <div class="flex items-center">
                    <span class="truncate">{{ column.name }}</span>
                    <i
                      class="fas fa-sort ml-1 text-xs"
                      :class="{
                        'fa-sort-up': sortBy === column.name && sortOrder === 'asc',
                        'fa-sort-down': sortBy === column.name && sortOrder === 'desc'
                      }"
                    ></i>
                  </div>
                </th>
                <th class="px-2 py-1 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-16">Actions</th>
              </tr>
            </thead>
            <tbody class="bg-white">
              <tr 
                v-for="(row, index) in data" 
                :key="row.id || row[columns[0]?.name]" 
                class="h-6 transition-colors"
                :class="{
                  'bg-green-50 border-l-2 border-l-green-500': isRowSelected(row),
                  'bg-blue-50 border-l-2 border-l-blue-500': !isRowSelected(row) && isRowSelectedForViewing(row),
                  'bg-gray-50': !isRowSelected(row) && !isRowSelectedForViewing(row) && index % 2 === 1,
                  'bg-white': !isRowSelected(row) && !isRowSelectedForViewing(row) && index % 2 === 0,
                  'hover:bg-gray-100': !isRowSelected(row) && !isRowSelectedForViewing(row)
                }"
              >
                <!-- Selection Checkbox -->
                <td class="px-2 py-0.5 w-8">
                  <input
                    type="checkbox"
                    :checked="isRowSelected(row)"
                    @change.stop="toggleRowSelection(row)"
                    class="w-3 h-3 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                </td>
                <td
                  v-for="column in displayedColumns"
                  :key="column.name"
                  class="px-2 py-0.5 whitespace-nowrap text-xs border-r border-gray-100 min-w-0 cursor-pointer"
                  :class="{
                    'text-green-900': isRowSelected(row),
                    'text-blue-900': !isRowSelected(row) && isRowSelectedForViewing(row),
                    'text-gray-900': !isRowSelected(row) && !isRowSelectedForViewing(row)
                  }"
                  style="width: auto; max-width: 200px;"
                  @click="selectRow(row)"
                >
                  <!-- Foreign Key Display -->
                  <div v-if="hasForeignKey(column.name) && row[column.name] && column.name === 'player_id'" class="max-w-xs">
                    <div class="inline-flex items-center">
                      <!-- Original ID value -->
                      <span class="text-gray-600 text-xs mr-2">{{ row[column.name] }}</span>
                      
                      <!-- Resolved display value -->
                      <span 
                        class="text-gray-400 hover:text-gray-700 cursor-pointer text-sm font-normal transition-colors duration-200"
                        :title="`${getForeignKeyDisplay(row, column.name)} (Click to view details)`"
                        @click="handleForeignKeyDetails({ fieldName: column.name, value: row[column.name], displayValue: getForeignKeyDisplay(row, column.name) })"
                      >
                        {{ getForeignKeyDisplay(row, column.name) }}
                      </span>
                      <i class="fas fa-external-link-alt text-xs text-gray-400 ml-1"></i>
                    </div>
                  </div>
                  
                  <!-- Regular Value Display -->
                  <div v-else class="max-w-xs truncate" :title="row[column.name]">
                    {{ row[column.name] }}
                  </div>
                </td>
                <td class="px-2 py-0.5 whitespace-nowrap text-right text-xs font-medium">
                  <button
                    @click.stop="editRow(row)"
                    class="text-indigo-600 hover:text-indigo-900 mr-1 p-0.5"
                    title="Edit Record"
                  >
                    <i class="fas fa-edit text-xs"></i>
                  </button>
                  <button
                    @click.stop="deleteRow(row[columns.find(col => col.key === 'PRI')?.name])"
                    class="text-red-600 hover:text-red-900 p-0.5"
                    title="Delete"
                  >
                    <i class="fas fa-trash text-xs"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <div v-if="data.length === 0" class="text-center py-8">
            <i class="fas fa-inbox text-2xl text-gray-300 mb-2"></i>
            <p class="text-gray-500 text-sm">No records found</p>
          </div>
        </div>
      </div>

      <!-- Footer (always visible) -->
      <div v-if="totalPages <= 1 && executionTime !== null" class="bg-white border-t border-gray-200 px-4 py-2 flex-shrink-0">
        <div class="flex items-center justify-end">
          <button 
            @click="toggleDiagnostics"
            class="text-xs text-gray-500 hover:text-blue-600 cursor-pointer transition-colors"
            title="Click to view detailed diagnostics"
          >
            <i class="fas fa-clock mr-1"></i>{{ executionTime }}ms
          </button>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="bg-white border-t border-gray-200 px-4 py-2 flex-shrink-0">
        <div class="flex items-center justify-between">
          <div class="text-xs text-gray-700 flex items-center space-x-4">
            <span>Page {{ currentPage }} of {{ totalPages }}</span>
            <button 
              v-if="executionTime !== null" 
              @click="toggleDiagnostics"
              class="text-gray-500 hover:text-blue-600 cursor-pointer transition-colors"
              title="Click to view detailed diagnostics"
            >
              <i class="fas fa-clock mr-1"></i>{{ executionTime }}ms
            </button>
          </div>
          <div class="flex space-x-1">
            <button
              @click="changePage(currentPage - 1)"
              :disabled="currentPage === 1"
              class="px-2 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              @click="changePage(currentPage + 1)"
              :disabled="currentPage === totalPages"
              class="px-2 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <!-- Edit Modal -->
      <div v-if="showEditModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
          <h3 class="text-lg font-semibold mb-4">Edit Record</h3>
          
          <div class="space-y-4">
            <div v-for="column in columns" :key="column.name" class="grid grid-cols-3 gap-4 items-center">
              <label class="block text-sm font-medium text-gray-700">{{ column.name }}</label>
              <div class="col-span-2">
                <input
                  v-if="!column.autoIncrement"
                  v-model="editingRow[column.name]"
                  type="text"
                  class="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                <span v-else class="text-gray-500">{{ editingRow[column.name] }} (auto)</span>
              </div>
            </div>
          </div>

          <div class="flex justify-end space-x-3 mt-6">
            <button
              @click="showEditModal = false; editingRow = null"
              class="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              @click="updateRow"
              class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Update
            </button>
          </div>
        </div>
      </div>

      <!-- Create Modal -->
      <div v-if="showCreateModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
          <h3 class="text-lg font-semibold mb-4">Create New Record</h3>
          
          <div class="space-y-4">
            <div v-for="column in columns.filter(col => !col.autoIncrement)" :key="column.name" class="grid grid-cols-3 gap-4 items-center">
              <label class="block text-sm font-medium text-gray-700">{{ column.name }}</label>
              <div class="col-span-2">
                <input
                  v-model="newRow[column.name]"
                  type="text"
                  class="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  :placeholder="column.default ? `Default: ${column.default}` : ''"
                >
              </div>
            </div>
          </div>

          <div class="flex justify-end space-x-3 mt-6">
            <button
              @click="showCreateModal = false"
              class="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              @click="createRow"
              class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create
            </button>
          </div>
        </div>
      </div>

      <!-- Record Editor -->
      <RecordEditor
        :show="showRecordEditor"
        :table-name="tableName"
        :record="isNewRecord ? {} : editingRow"
        :columns="columns"
        :is-new="isNewRecord"
        @close="closeRecordEditor"
        @saved="handleRecordSaved"
      />


    </div>
  </div>
</template>

<style scoped>
/* Column search highlight animation */
.column-highlight {
  background: linear-gradient(45deg, #4f46e5, #3730a3) !important;
  color: white !important;
  transform: scale(1.02);
  box-shadow: 0 4px 8px rgba(79, 70, 229, 0.3);
  transition: all 0.3s ease;
  border-radius: 4px;
  position: relative;
  z-index: 20;
}

.column-highlight * {
  color: white !important;
}

/* Smooth transitions for column controls */
.transition-colors {
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

/* Export dropdown animation */
.export-dropdown {
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from { 
    opacity: 0; 
    transform: translateY(-10px) scale(0.95); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0) scale(1); 
  }
}

/* Checkbox indeterminate state */
input[type="checkbox"]:indeterminate {
  background-color: #10b981;
  border-color: #10b981;
  background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M4 8h8'/%3e%3c/svg%3e");
}
</style>